import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Search,
  Loader2,
  Info,
  CheckCircle,
  XCircle,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getSourceTypeBadge = (type) => {
  const colors = {
    PORTAL: 'bg-blue-100 text-blue-800 border-blue-200',
    NEWSPAPER: 'bg-purple-100 text-purple-800 border-purple-200',
    OWNER_REFERENCE: 'bg-teal-100 text-teal-800 border-teal-200',
    CUSTOM_PROVIDER: 'bg-amber-100 text-amber-800 border-amber-200'
  };
  return <Badge className={colors[type] || 'bg-slate-100 text-slate-800'}>{type}</Badge>;
};

export const SearchSection = ({ token, isMainUser, onStatsUpdate }) => {
  const [searchMode, setSearchMode] = useState('single');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [bulkSearchInput, setBulkSearchInput] = useState('');
  const [bulkSearchResults, setBulkSearchResults] = useState(null);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      toast.error('Search query must be at least 2 characters');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/source/search?q=${encodeURIComponent(searchQuery)}`,
        axiosConfig
      );
      setSearchResults(response.data.results);
      if (response.data.results.length === 0) {
        toast.info('No results found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleBulkSearch = async () => {
    const queries = bulkSearchInput.trim().split('\n').filter(line => line.trim()).map(line => line.trim());
    
    if (queries.length === 0) {
      toast.error('Please enter at least one search value');
      return;
    }

    if (queries.length > 10000) {
      toast.error('Maximum 10,000 entries allowed');
      return;
    }

    setSearching(true);
    setBulkSearchResults(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/source/bulk-search`,
        { queries },
        axiosConfig
      );
      setBulkSearchResults(response.data);
      toast.success(`Found ${response.data.found} of ${response.data.total_searched} entries`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleDelete = async (sourceId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      await axios.delete(`${API_URL}/api/source/${sourceId}`, axiosConfig);
      toast.success('Record deleted');
      setSearchResults(prev => prev.filter(r => r.id !== sourceId));
      if (onStatsUpdate) onStatsUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const copyBulkSearchResults = (filterType) => {
    if (!bulkSearchResults) return;
    
    const data = bulkSearchResults.results || [];
    let dataToCopy;
    if (filterType === 'found') {
      dataToCopy = data.filter(r => r.found).map(r => r.query);
    } else {
      dataToCopy = data.filter(r => !r.found).map(r => r.query);
    }
    
    navigator.clipboard.writeText(dataToCopy.join('\n'));
    toast.success(`Copied ${dataToCopy.length} entries`);
  };

  const bulkEntryCount = bulkSearchInput.trim() ? bulkSearchInput.trim().split('\n').filter(l => l.trim()).length : 0;

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={searchMode === 'single' ? 'default' : 'outline'}
          onClick={() => setSearchMode('single')}
          size="sm"
          data-testid="single-search-mode"
        >
          Single Search
        </Button>
        <Button
          variant={searchMode === 'bulk' ? 'default' : 'outline'}
          onClick={() => setSearchMode('bulk')}
          size="sm"
          data-testid="bulk-search-mode"
        >
          Bulk Search
        </Button>
      </div>

      {searchMode === 'single' && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="Search by URL, mobile number, or Source ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="search-input"
                className="flex-1"
              />
              <Button onClick={handleSearch} disabled={searching} data-testid="search-button">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {searchMode === 'bulk' && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Search className="h-5 w-5" />
              Bulk Search
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg border border-border">
              <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                Paste multiple values (one per line) to search. Max 10,000 entries.
              </p>
            </div>
            <Textarea
              placeholder="9876543210"
              value={bulkSearchInput}
              onChange={(e) => setBulkSearchInput(e.target.value)}
              className="min-h-[200px] font-mono text-sm"
              data-testid="bulk-search-textarea"
            />
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{bulkEntryCount} entries</span>
              <Button
                onClick={handleBulkSearch}
                disabled={searching || !bulkSearchInput.trim()}
                data-testid="bulk-search-button"
                className="gap-2"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Search All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {bulkSearchResults && searchMode === 'bulk' && (
        <BulkResultsCard 
          results={bulkSearchResults} 
          onCopyFound={() => copyBulkSearchResults('found')}
          onCopyNotFound={() => copyBulkSearchResults('not_found')}
        />
      )}

      {searchResults.length > 0 && searchMode === 'single' && (
        <SingleSearchResultsCard 
          results={searchResults} 
          isMainUser={isMainUser} 
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

const BulkResultsCard = ({ results, onCopyFound, onCopyNotFound }) => {
  const items = results.results || [];
  const foundCount = results.found || 0;
  const notFoundCount = results.not_found || 0;
  
  return (
    <Card className="border-border">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-lg font-heading">
          Bulk Search Results
          <span className="text-sm font-normal text-muted-foreground ml-2">
            ({foundCount} found, {notFoundCount} not found)
          </span>
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCopyFound} className="gap-2">
            <CheckCircle className="h-4 w-4" />Copy Found
          </Button>
          <Button variant="outline" size="sm" onClick={onCopyNotFound} className="gap-2">
            <XCircle className="h-4 w-4" />Copy Not Found
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background">
              <TableRow className="bg-muted/50">
                <TableHead className="w-16">#</TableHead>
                <TableHead>Query</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source Type</TableHead>
                <TableHead>Source UID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => (
                <BulkResultRow key={index} item={item} index={index} />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

const BulkResultRow = ({ item, index }) => {
  const found = item.found;
  const query = item.query || '';
  const result = item.result;
  const sourceType = result ? result.source_type : null;
  const sourceUid = result ? result.source_uid : null;
  const rowClass = found ? 'bg-emerald-50/50' : 'bg-red-50/50';
  
  return (
    <TableRow className={`hover:bg-muted/30 ${rowClass}`}>
      <TableCell className="font-mono text-sm">{index + 1}</TableCell>
      <TableCell className="max-w-[200px] truncate font-mono text-sm" title={query}>{query}</TableCell>
      <TableCell>
        {found ? (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
            <CheckCircle className="h-3 w-3 mr-1" />Found
          </Badge>
        ) : (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />Not Found
          </Badge>
        )}
      </TableCell>
      <TableCell>{sourceType ? getSourceTypeBadge(sourceType) : '-'}</TableCell>
      <TableCell className="font-mono text-sm text-secondary">{sourceUid || '-'}</TableCell>
    </TableRow>
  );
};

const SingleSearchResultsCard = ({ results, isMainUser, onDelete }) => {
  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-lg font-heading">Search Results ({results.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Raw Value</TableHead>
                <TableHead>Source Type</TableHead>
                <TableHead>Source Name</TableHead>
                <TableHead>Source UID</TableHead>
                <TableHead>Created</TableHead>
                {isMainUser && <TableHead className="w-16">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.map((result) => (
                <SingleResultRow key={result.id} result={result} isMainUser={isMainUser} onDelete={onDelete} />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

const SingleResultRow = ({ result, isMainUser, onDelete }) => {
  const rawValue = result.raw_value || '';
  const sourceType = result.source_type;
  const sourceName = result.source_name || '-';
  const sourceUid = result.source_uid || '';
  const createdAt = result.created_at ? new Date(result.created_at).toLocaleDateString() : '-';
  
  return (
    <TableRow className="hover:bg-muted/30">
      <TableCell className="max-w-[200px] truncate font-mono text-sm" title={rawValue}>{rawValue}</TableCell>
      <TableCell>{getSourceTypeBadge(sourceType)}</TableCell>
      <TableCell className="text-sm">{sourceName}</TableCell>
      <TableCell className="font-mono text-sm text-secondary">{sourceUid}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{createdAt}</TableCell>
      {isMainUser && (
        <TableCell>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(result.id)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TableCell>
      )}
    </TableRow>
  );
};

export default SearchSection;
